import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { ContactCreateUpdateRequest } from '@/types/api';

interface AddContactManuallyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (contactData: ContactCreateUpdateRequest) => void;
}

const AddContactManuallyModal = ({
  isOpen,
  onClose,
  onSave,
}: AddContactManuallyModalProps) => {
  const [contactName, setContactName] = useState('');
  const [error, setError] = useState('');

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setContactName('');
      setError('');
    }
  }, [isOpen]);

  const handleSave = () => {
    // Validate input
    const trimmedName = contactName.trim();

    if (!trimmedName) {
      setError('Please enter a contact name');
      return;
    }

    if (trimmedName.length < 2) {
      setError('Name must be at least 2 characters');
      return;
    }

    // Clear error and save
    setError('');
    const contactData: ContactCreateUpdateRequest = {
      name: trimmedName,
    };
    onSave(contactData);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSave();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[621px] p-0 rounded-[8px] border-0">
        <div className="p-8 md:p-[56px]">
          {/* Header */}
          <div className="mb-8">
            <p className="text-[#6C4F77]/50 text-sm font-normal mb-2">
              Step 2 of 3 • Add one contact
            </p>
            <h1 className="text-[20px] md:text-[32px] font-bold text-[#6C4F77] mb-2 font-modernist">
              Add contact
            </h1>
            <p className="text-[#666666] text-[14px] md:text-[16px] font-normal">
              Add your contacts so WabiSabi can help you remember the people you care about. Start with 1, add the rest later.
            </p>
          </div>

          {/* Contact Name Input */}
          <div className="space-y-2 mb-8">
            <Label htmlFor="contact-name" className="text-[#666666] text-sm">
              Add a contact
            </Label>
            <Input
              id="contact-name"
              type="text"
              placeholder="Ex. John Smith"
              value={contactName}
              onChange={(e) => {
                setContactName(e.target.value);
                setError(''); // Clear error on input change
              }}
              onKeyPress={handleKeyPress}
              className={`h-12 border-gray-200 focus:border-[#6C4F77] focus:ring-[#6C4F77] ${
                error ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
              }`}
              autoFocus
            />
            {error && (
              <p className="text-red-500 text-sm mt-1">{error}</p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1 border-gray-200 text-gray-600 font-normal text-[14px] font-modernist py-[20px]"
            >
              Back
            </Button>
            <Button
              onClick={handleSave}
              className="flex-1 bg-[#6C4F77] hover:bg-[#5A3A68] text-white font-bold text-[14px] font-modernist py-[20px]"
            >
              Save & Next
            </Button>
          </div>

          {/* Privacy Note */}
          <p className="text-center text-[12px] text-gray-500 mt-4">
            You can change or remove contacts anytime. Your privacy is always respected.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddContactManuallyModal;